import type { DragEndEvent } from '@dnd-kit/core';
import type { ReactNode } from 'react';
import type { AttachmentDto, UploadedFile } from '../types';
import type { PixabaySearchPanelProps } from './pixabay-search-panel';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState } from 'react';
import { uploadthingFileUrl } from '../client/url';
import css from './added-images.module.scss';
import { ImageCropModal } from './image-crop-modal';
import { PixabaySearchPanel } from './pixabay-search-panel';
import { UploadButton } from './upload-button';

export type AddedImagesProps = {
  value: AttachmentDto[];
  onChange: (next: AttachmentDto[]) => void;
  // Upload trigger for new files (e.g. wrapped `useUploadThing` startUpload).
  startUpload: (files: File[]) => Promise<UploadedFile[] | undefined>;
  isUploading?: boolean;
  // Optional: when a tile is deleted, the consumer can synchronously
  // call `trpc.uploadthing.deleteFiles` to purge the orphan UT key.
  onDeleteKey?: (key: string) => void;
  enableThumbnail?: boolean;
  enableCrop?: boolean;
  // Pixabay integration. When provided, a "Pixabay" toolbar button
  // surfaces the search panel + selected images are uploaded via
  // `uploadFromUrl`, mirroring legacy flow.
  pixabay?: {
    search: PixabaySearchPanelProps['onSearch'];
    uploadFromUrl: (urls: string[]) => Promise<UploadedFile[]>;
  };
  emptyHint?: ReactNode;
};

const toAttachment = (file: UploadedFile, order: number): AttachmentDto => ({
  id: file.key,
  fileKey: file.key,
  url: file.url || uploadthingFileUrl(file.key),
  order,
  isThumbnail: false,
});

type SortableTileProps = {
  attachment: AttachmentDto;
  isThumbnail: boolean;
  onSetThumbnail?: () => void;
  onCrop?: () => void;
  onDelete: () => void;
};

const SortableTile = ({
  attachment,
  isThumbnail,
  onSetThumbnail,
  onCrop,
  onDelete,
}: SortableTileProps) => {
  const {
    setNodeRef,
    listeners,
    attributes,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: attachment.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className={[
        css.tile,
        isThumbnail ? css.tileThumb : '',
        isDragging ? css.tileDragging : '',
      ]
        .filter(Boolean)
        .join(' ')}
      ref={setNodeRef}
      style={style}
    >
      {isThumbnail && <span className={css.thumbnailBadge}>Thumb</span>}
      <img
        alt={attachment.caption ?? ''}
        className={css.tileImage}
        src={attachment.url}
        {...attributes}
        {...listeners}
      />
      <div className={css.tileActions}>
        {onSetThumbnail && (
          <button onClick={onSetThumbnail} type="button">
            {isThumbnail ? '★' : '☆'}
          </button>
        )}
        {onCrop && (
          <button onClick={onCrop} type="button">
            Crop
          </button>
        )}
        <button onClick={onDelete} type="button">
          Delete
        </button>
      </div>
    </div>
  );
};

export const AddedImages = ({
  value,
  onChange,
  startUpload,
  isUploading,
  onDeleteKey,
  enableThumbnail = true,
  enableCrop = true,
  pixabay,
  emptyHint = 'No images yet — upload files or pick from Pixabay.',
}: AddedImagesProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );
  const [pixabayOpen, setPixabayOpen] = useState(false);
  const [cropTarget, setCropTarget] = useState<AttachmentDto | null>(null);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = value.findIndex((a) => a.id === active.id);
    const newIndex = value.findIndex((a) => a.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(value, oldIndex, newIndex).map((a, idx) => ({
      ...a,
      order: idx,
    }));
    onChange(reordered);
  };

  const handleUploadComplete = async (files: File[]): Promise<undefined> => {
    const uploaded = (await startUpload(files)) ?? [];
    if (uploaded.length === 0) return undefined;
    const baseOrder = value.length;
    const additions = uploaded.map((file, idx) =>
      toAttachment(file, baseOrder + idx)
    );
    onChange([...value, ...additions]);
    return undefined;
  };

  const handleSetThumbnail = (id: string) => {
    onChange(value.map((a) => ({ ...a, isThumbnail: a.id === id })));
  };

  const handleDelete = (attachment: AttachmentDto) => {
    onChange(
      value
        .filter((a) => a.id !== attachment.id)
        .map((a, idx) => ({ ...a, order: idx }))
    );
    if (attachment.fileKey) onDeleteKey?.(attachment.fileKey);
  };

  const handleCropped = async (file: File) => {
    if (!cropTarget) return;
    const uploaded = (await startUpload([file])) ?? [];
    const replacement = uploaded[0];
    if (!replacement) return;
    onChange(
      value.map((a) =>
        a.id === cropTarget.id
          ? {
              ...a,
              id: replacement.key,
              fileKey: replacement.key,
              url: replacement.url || uploadthingFileUrl(replacement.key),
            }
          : a
      )
    );
    if (cropTarget.fileKey) onDeleteKey?.(cropTarget.fileKey);
    setCropTarget(null);
  };

  const handlePixabaySelect = async (images: { largeImageURL: string }[]) => {
    if (!pixabay) return;
    const uploaded = await pixabay.uploadFromUrl(
      images.map((image) => image.largeImageURL)
    );
    const baseOrder = value.length;
    const additions = uploaded.map((file, idx) =>
      toAttachment(file, baseOrder + idx)
    );
    onChange([...value, ...additions]);
    setPixabayOpen(false);
  };

  return (
    <div className={css.root}>
      <div className={css.toolbar}>
        <UploadButton
          accept="image/*"
          isUploading={isUploading}
          label="Upload"
          modalTitle="Upload images"
          multiple
          startUpload={handleUploadComplete}
        />
        {pixabay && (
          <button
            className={css.toolbarButton}
            onClick={() => setPixabayOpen((prev) => !prev)}
            type="button"
          >
            Pixabay
          </button>
        )}
      </div>

      {pixabayOpen && pixabay && (
        <PixabaySearchPanel
          multiSelect
          onSearch={pixabay.search}
          onSelect={handlePixabaySelect}
        />
      )}

      {value.length === 0 && <div className={css.empty}>{emptyHint}</div>}

      {value.length > 0 && (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={value.map((a) => a.id)}
            strategy={rectSortingStrategy}
          >
            <div className={css.grid}>
              {value.map((attachment) => (
                <SortableTile
                  attachment={attachment}
                  isThumbnail={Boolean(attachment.isThumbnail)}
                  key={attachment.id}
                  onCrop={
                    enableCrop ? () => setCropTarget(attachment) : undefined
                  }
                  onDelete={() => handleDelete(attachment)}
                  onSetThumbnail={
                    enableThumbnail
                      ? () => handleSetThumbnail(attachment.id)
                      : undefined
                  }
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {cropTarget && (
        <ImageCropModal
          onClose={() => setCropTarget(null)}
          onCropped={handleCropped}
          open
          src={cropTarget.url}
        />
      )}
    </div>
  );
};
